import { useMemo, useState, type ComponentProps } from 'react';
import {
  Banner,
  Box,
  Button,
  DataTable,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateTitle,
  FormattedRelativeTime,
  InlineStack,
  Link,
  Stack,
  StatusIndicator,
  Text,
  type DataTableFeatures,
} from '@customerio/pluma-components/react';
import { createColumnHelper, type Table as TanstackTable } from '@tanstack/table-core';
import {
  TRIGGERED_BROADCAST_TYPE,
  usageActionLabel,
  type CollectionUsageCampaign,
} from '@/services/db/collection/usage';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { CampaignStateLabels } from '@/utils/campaign-labels';
import { isEmptyString } from '@/utils/string';

const COLLECTIONS_DOCS_URL = 'https://docs.customer.io/journeys/collections/';

type StatusColor = ComponentProps<typeof StatusIndicator>['color'];

// Transitional states take the colour they are heading for, so a stopping campaign doesn't
// read as running.
const STATE_COLORS: Record<string, StatusColor> = {
  running: 'green',
  draft: 'grey',
  stopped: 'yellow',
  stopping: 'yellow',
  sunsetting: 'yellow',
  archived: 'grey',
  archiving: 'grey',
  unarchiving: 'grey',
};

/**
 * services reports `archived` alongside the FSM state rather than as one, and archived is the
 * reading that matters here — an archived-but-running campaign still blocks the delete.
 */
function campaignStatus(campaign: CollectionUsageCampaign): { label: string; color: StatusColor } {
  const state = campaign.archived ? 'archived' : campaign.state;
  return { label: CampaignStateLabels[state] ?? 'Unknown', color: STATE_COLORS[state] ?? 'grey' };
}

const columnHelper = createColumnHelper<DataTableFeatures, CollectionUsageCampaign>();

const isBroadcast = (campaign: CollectionUsageCampaign) => campaign.type === TRIGGERED_BROADCAST_TYPE;

/** API-triggered broadcasts are campaigns in services, but they have their own URL space. */
function campaignHref(campaign: CollectionUsageCampaign, workspaceId: number): string {
  return isBroadcast(campaign)
    ? generatePath('/workspaces/:workspaceId/journeys/broadcasts/broadcast/:broadcastId/overview', {
        workspaceId,
        broadcastId: campaign.id,
      })
    : generatePath('/workspaces/:workspaceId/journeys/campaigns/:campaignId/overview', {
        workspaceId,
        campaignId: campaign.id,
      });
}

type CollectionUsageEmptyStateProps<TData extends CollectionUsageCampaign = CollectionUsageCampaign> = {
  table: TanstackTable<DataTableFeatures, TData>;
};

type NameCellProps = { campaign: CollectionUsageCampaign; workspaceId: number };

function NameCell({ campaign, workspaceId }: NameCellProps) {
  // An action with neither a name nor an attribute has nothing to show — see `usageActionLabel`.
  const actions = campaign.actions.map(usageActionLabel).filter((label) => label.length > 0);

  return (
    <Stack gap="025">
      <Box truncate>
        <Link href={campaignHref(campaign, workspaceId)}>{campaign.name}</Link>
      </Box>
      {actions.length > 0 ? (
        <Text text="product-pxs" color="subtle">
          {actions.join(', ')}
        </Text>
      ) : null}
    </Stack>
  );
}

type CollectionUsageTableProps = {
  campaigns: CollectionUsageCampaign[];
  workspaceId: number;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
};

type TypeView = 'campaigns' | 'broadcasts';

function isTypeView(value: string | null): value is TypeView {
  return value === 'campaigns' || value === 'broadcasts';
}

/** Campaigns and API-triggered broadcasts that query this collection. */
export function CollectionUsageTable({
  campaigns,
  workspaceId,
  isLoading,
  hasError,
  onRetry,
}: CollectionUsageTableProps) {
  const taxonomy = useProductTaxonomy();
  const [activeView, setActiveView] = useState<TypeView | null>(null);

  const broadcastCount = useMemo(() => campaigns.filter(isBroadcast).length, [campaigns]);
  const visibleCampaigns = useMemo(() => {
    if (activeView == null) return campaigns;
    const wantBroadcasts = activeView === 'broadcasts';
    return campaigns.filter((campaign) => isBroadcast(campaign) === wantBroadcasts);
  }, [campaigns, activeView]);

  const columns = useMemo(() => {
    return [
      // Must stay an accessor column: the global filter behind "Search by name" matches on
      // accessor values, and a display column exposes none.
      columnHelper.accessor('name', {
        id: 'name',
        header: 'Name',
        meta: { shouldWrapCellContent: true, width: '2fr' },
        cell: (info) => <NameCell campaign={info.row.original} workspaceId={workspaceId} />,
      }),
      columnHelper.display({
        id: 'type',
        header: 'Type',
        meta: { width: '1.5fr' },
        // No taxonomy term exists for API-triggered broadcasts, so that name is fixed while
        // the campaign one follows the Campaign → Automation rename.
        cell: (info) => (
          <Text>
            {isBroadcast(info.row.original) ? 'API-triggered broadcast' : taxonomy.term('campaigns.titleSingular')}
          </Text>
        ),
      }),
      columnHelper.display({
        id: 'status',
        header: 'Status',
        meta: { width: '1fr' },
        cell: (info) => {
          const status = campaignStatus(info.row.original);
          return (
            <InlineStack alignBlock="center" gap="100">
              <StatusIndicator color={status.color} />
              <Text>{status.label}</Text>
            </InlineStack>
          );
        },
      }),
      // services returns rows newest-updated-first, so this column is what explains the order.
      columnHelper.display({
        id: 'updated',
        header: 'Updated',
        meta: { width: '1fr' },
        cell: (info) => {
          const updated = info.row.original.updated;
          return updated == null ? <Text>—</Text> : <FormattedRelativeTime date={updated} withTooltip={true} />;
        },
      }),
    ];
  }, [taxonomy, workspaceId]);

  // Kept apart because they need opposite treatment: the banner reports a failed refresh over
  // rows we still have, leaving the empty state free to blame the filter instead of the request.
  const hasStaleRows = hasError && campaigns.length > 0;
  const hasNothingLoaded = hasError && campaigns.length === 0;

  // One empty state covers three situations, and the middle one depends on the live search
  // term — hence built here rather than at module scope.
  const EmptyStateComponent = useMemo(() => {
    function CollectionUsageEmptyState<TData extends CollectionUsageCampaign = CollectionUsageCampaign>({
      table,
    }: CollectionUsageEmptyStateProps<TData>) {
      if (hasNothingLoaded) {
        return (
          <EmptyState>
            <EmptyStateTitle icon="database">Sorry, something went wrong</EmptyStateTitle>
            <EmptyStateDescription>We couldn&apos;t load what uses this collection.</EmptyStateDescription>
            <EmptyStateActions>
              <Button variant="secondary" onClick={onRetry}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyState>
        );
      }

      // A filter can only miss if there was something to miss: with no usage at all, picking a
      // view must still say the collection is unused.
      const globalFilter = table.store.state.globalFilter;
      const search = typeof globalFilter === 'string' ? globalFilter : undefined;
      if (campaigns.length > 0 && (activeView != null || !isEmptyString(search))) {
        return (
          <EmptyState>
            <EmptyStateTitle icon="search">No matches</EmptyStateTitle>
            <EmptyStateDescription>
              Nothing here matches the current filter. Try a different term or type.
            </EmptyStateDescription>
          </EmptyState>
        );
      }

      return (
        <EmptyState>
          <EmptyStateTitle icon="database">This collection isn&apos;t used yet</EmptyStateTitle>
          {/* Not the design's "reference it with Liquid" copy: services has no Liquid namespace
              for collections, so a query-collection action is the only way to use one. */}
          <EmptyStateDescription>
            {`Add a "Query a collection" action to a ${taxonomy.term('campaigns.singular')} to look up rows from this collection. Anything that uses it shows up here.`}
          </EmptyStateDescription>
          <EmptyStateActions>
            <Button href={generatePath('/workspaces/:workspaceId/journeys/campaigns/new', { workspaceId })}>
              {`Create a ${taxonomy.term('campaigns.singular')}`}
            </Button>
            <Button variant="secondary" href={COLLECTIONS_DOCS_URL} isExternal>
              How to use collections
            </Button>
          </EmptyStateActions>
        </EmptyState>
      );
    }

    return CollectionUsageEmptyState;
  }, [campaigns.length, hasNothingLoaded, activeView, onRetry, taxonomy, workspaceId]);

  return (
    <Stack gap="200">
      {hasStaleRows ? (
        <Banner
          variant="caution"
          description="We couldn't refresh this list, so it may be out of date."
          action={{ label: 'Try again', onClick: onRetry }}
        />
      ) : null}
      <DataTable
        caption="Collection usage"
        data={visibleCampaigns}
        columns={columns}
        getRowId={(campaign: CollectionUsageCampaign) => campaign.id}
        isLoading={isLoading}
        loadingState={{ rowCount: 3 }}
        withSavedViews
        // Counts come from the whole response, so they hold steady as views change.
        savedViews={{
          defaultViewName: `All (${campaigns.length})`,
          activeViewId: activeView,
          onActiveViewChange: (id: string | null) => setActiveView(isTypeView(id) ? id : null),
          views: [
            {
              id: 'campaigns',
              name: `${taxonomy.term('campaigns.titlePlural')} (${campaigns.length - broadcastCount})`,
            },
            { id: 'broadcasts', name: `API-triggered broadcasts (${broadcastCount})` },
          ],
        }}
        withSearch
        // Without these two, Pluma's Search only pushes to the global filter on Enter/blur, so
        // the list wouldn't narrow while typing.
        search={{ placeholder: 'Search by name', shouldSearchOnInput: true, shouldSearchOnClear: true }}
        emptyStateComponent={EmptyStateComponent}
      />
    </Stack>
  );
}
